import React, { useRef, useState, useEffect } from 'react';

import { useTheme } from 'styled-components';

import { IoIosSend } from 'react-icons/io';
import { FaCopy } from 'react-icons/fa';
import { FaAngleDoubleDown } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { IoMdChatboxes } from 'react-icons/io';
import { FaRegWindowMaximize } from 'react-icons/fa';

import Moment from 'moment';

import api from '~/services/api';

import { copyToClipBoard } from '~/helpers/clipboad';

import { useIAmode } from '~/hooks/iamode';

import { Typing, Loading } from '~/components';

import {
  MainArea,
  MainAreaContent,
  InputArea,
  Message,
  Header,
  Footer,
  Question,
  ToBottom,
} from './styles';

import MensagemType from '~/@types/mensagem';

let lang = require('moment/locale/pt-br');
Moment.locale('pt-br', lang);

const Chat: React.FC = () => {
  const { toggleIAmode } = useIAmode();
  const { colors } = useTheme();

  const anchorRef = useRef<null | HTMLDivElement>(null);
  const inputRef = useRef<null | HTMLInputElement>(null);

  const [inLoad, setInLoad] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isEnded, setIsEnded] = useState(false);

  const [mensagens, setMensagens] = useState<MensagemType[]>([]);

  const scrollToBottom = () => {
    anchorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const focusInInput = () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const enviarMensagem = async (pPergunta: string) => {
    try {
      setInLoad(true);
      setInputMessage('');
      scrollToBottom();

      const cache = [
        ...mensagens,
        {
          direcao: 'ia',
          dt: `${new Date()}`,
          id: `${new Date().getTime()}`,
          mensagem: pPergunta,
        },
      ];

      setMensagens(cache);

      setTimeout(() => {
        scrollToBottom();
      }, 300);

      const dadosDeEntrada = new FormData();

      dadosDeEntrada.append('pergunta', pPergunta);
      dadosDeEntrada.append('debug', '0');

      const response = await api.post(`conversacao`, dadosDeEntrada, {
        baseURL: 'https://commerceplus.info/apps/chatgestor/api',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { data } = response;

      setTimeout(() => {
        if (data && Array.isArray(data) && data.length) {
          setMensagens([...cache, ...data]);
          scrollToBottom();
        }
        setInLoad(false);
        focusInInput();
      }, 300);
    } catch (e) {
      setInLoad(false);
    }
  };

  useEffect(() => {
    const buscarHistorico = async () => {
      try {
        setInLoad(true);

        const response = await api.get(`historico`, {
          baseURL: 'https://commerceplus.info/apps/chatgestor/api',
        });

        const { data } = response;

        setTimeout(() => {
          if (data && Array.isArray(data) && data.length) {
            setMensagens([...data]);
            scrollToBottom();
          }
          setInLoad(false);
          focusInInput();
        }, 300);
      } catch (e) {
        setInLoad(false);
      }
    };

    buscarHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gotoToChatMode = () => {
    window.open('https://commerceplus.info/apps/chatgestor', '_blank');
  };

  const scrollRef = useRef<null | HTMLDivElement>(null);

  const onScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight;
      setIsEnded(isNearBottom ? false : true);
    }
  };

  return (
    <MainArea>
      <Header>
        <i>
          <IoMdChatboxes size={32} color={colors.IC1} />
        </i>
        <div>
          <h1>Chat Gestor</h1>
          <h2>IA</h2>
          <small>BETA</small>
        </div>

        <button onClick={gotoToChatMode}>
          <FaRegWindowMaximize size={22} color={colors.IC1} />
        </button>
        <button onClick={toggleIAmode}>
          <IoMdClose size={32} color={colors.IC1} />
        </button>
      </Header>
      <MainAreaContent ref={scrollRef} onScroll={onScroll}>
        {mensagens.map(mensagem => {
          if (
            mensagem.direcao === 'usuario' &&
            (mensagem.tipo === 'prompt' || mensagem.tipo !== 'prompt')
          ) {
            return (
              <Message className={mensagem.tipo === 'prompt' ? 'prompt' : ''}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: `${mensagem.mensagem}` || '',
                  }}
                ></div>
                {mensagem.tipo === 'prompt' ? (
                  <button
                    onClick={() =>
                      copyToClipBoard(`${mensagem.mensagem || ''}`)
                    }
                  >
                    <FaCopy size={20} color={colors.IC1} />
                  </button>
                ) : null}
                <small>{Moment(mensagem.dt).format('HH:mm')}</small>
              </Message>
            );
          }
          return (
            <Question>
              <strong>{mensagem.mensagem}</strong>
              <small>{Moment(mensagem.dt).format('HH:mm')}</small>
            </Question>
          );
        })}
        <div ref={anchorRef}></div>
        {inLoad ? <Typing /> : null}
      </MainAreaContent>

      <Footer>
        <InputArea>
          <input
            ref={inputRef}
            placeholder="Digite uma mensagem"
            disabled={inLoad}
            value={inputMessage}
            onChange={e => setInputMessage(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                enviarMensagem(e.currentTarget.value);
              }
            }}
          />

          {!inLoad ? (
            <>
              <button
                onClick={() => {
                  if (inputMessage) {
                    enviarMensagem(inputMessage);
                  }
                }}
                disabled={inLoad}
              >
                <IoIosSend size={26} color={colors.IC1} />
              </button>
            </>
          ) : (
            <Loading color={colors.IC1} />
          )}
        </InputArea>
      </Footer>

      {isEnded ? (
        <ToBottom onClick={scrollToBottom}>
          <FaAngleDoubleDown size={22} color={colors.IC1} />
        </ToBottom>
      ) : null}
    </MainArea>
  );
};

export default Chat;
